<div class="verification-wrapper" [class.has-viewer]="viewerDoc">
<div class="verification-container">

  <!-- Back button -->
  <button class="btn-back" (click)="goBack()">← {{ 'verification.back' | translate }}</button>

  <h2>{{ 'verification.title' | translate }}</h2>
  <p class="subtitle" *ngIf="dossier">{{ dossier.external_id }}</p>

  <!-- Loading -->
  <div class="loading" *ngIf="loading">{{ 'dossier.loading' | translate }}</div>

  <!-- Error -->
  <div class="error-message" *ngIf="error">{{ error }}</div>

  <ng-container *ngIf="!loading && dossier">

    <!-- Summary bar -->
    <div class="summary-bar">
      <div class="summary-item">
        <span class="summary-label">{{ 'verification.documents' | translate }}</span>
        <span class="summary-value">{{ totalDocs }}</span>
        <span class="summary-detail">
          <span class="dot original"></span>{{ originalDocs }} {{ 'verification.originals' | translate }}
          <span class="dot copy"></span>{{ copyDocs }} {{ 'verification.copies' | translate }}
          <span class="dot unassessed"></span>{{ unassessedDocs }} {{ 'verification.unassessed' | translate }}
        </span>
      </div>
      <div class="summary-item">
        <span class="summary-label">{{ 'verification.checks' | translate }}</span>
        <span class="summary-value">{{ totalChecks }}</span>
        <span class="summary-detail">
          <span class="dot ok"></span>{{ okChecks }} OK
          <span class="dot fail"></span>{{ failChecks }} Fail
          <span class="dot pending"></span>{{ pendingChecks }} {{ 'verification.pending' | translate }}
        </span>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tab-bar">
      <button class="tab" [class.active]="activeTab === 'originals'" (click)="activeTab = 'originals'">
        {{ 'verification.tabOriginals' | translate }}
      </button>
      <button class="tab" [class.active]="activeTab === 'checks'" (click)="activeTab = 'checks'">
        {{ 'verification.tabChecks' | translate }}
      </button>
      <button class="tab" [class.active]="activeTab === 'discrepancies'" (click)="activeTab = 'discrepancies'">
        {{ 'verification.tabDiscrepancies' | translate }}
        <span class="tab-badge" *ngIf="discrepancyKpi.total > 0">{{ discrepancyKpi.total }}</span>
      </button>
    </div>

    <!-- Originals tab -->
    <div class="tab-content" *ngIf="activeTab === 'originals'">
      <p class="tab-hint">{{ 'verification.originalsHint' | translate }}</p>

      <div class="bulk-original-actions" *ngIf="documents.length > 0">
        <button class="btn-flag-all" [disabled]="flaggingAll" (click)="flagAll(true)">
          {{ 'verification.flagAllOriginal' | translate }}
        </button>
        <button class="btn-flag-all" [disabled]="flaggingAll" (click)="flagAll(false)">
          {{ 'verification.flagAllCopy' | translate }}
        </button>
      </div>

      <div class="empty-state" *ngIf="documents.length === 0">
        {{ 'verification.noDocuments' | translate }}
      </div>

      <table class="originals-table" *ngIf="documents.length > 0">
        <thead>
          <tr>
            <th>#</th>
            <th></th>
            <th>{{ 'verification.file' | translate }}</th>
            <th>{{ 'verification.docType' | translate }}</th>
            <th>{{ 'verification.confidence' | translate }}</th>
            <th>{{ 'verification.pagesCol' | translate }}</th>
            <th>{{ 'verification.originalStatus' | translate }}</th>
            <th>{{ 'verification.actions' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let doc of documents; let i = index">
            <td>{{ i + 1 }}</td>
            <td class="thumbnail-cell" (click)="openViewer(doc)">
              <img *ngIf="pageImageUrls[doc.oid]"
                   [src]="pageImageUrls[doc.oid]"
                   class="doc-thumbnail clickable"
                   [class.selected]="viewerDoc?.oid === doc.oid"
                   [alt]="getUploadFilename(doc)" />
              <div *ngIf="!pageImageUrls[doc.oid]" class="doc-thumbnail-placeholder clickable"></div>
            </td>
            <td class="filename-cell">{{ getUploadFilename(doc) }}</td>
            <td>{{ doc.document_type }}</td>
            <td>
              <span class="confidence-badge" *ngIf="doc.confidence != null"
                    [class.green]="doc.confidence > 0.95"
                    [class.light-green]="doc.confidence > 0.90 && doc.confidence <= 0.95"
                    [class.yellow]="doc.confidence > 0.85 && doc.confidence <= 0.90"
                    [class.orange]="doc.confidence >= 0.80 && doc.confidence <= 0.85"
                    [class.red]="doc.confidence < 0.80">
                {{ doc.confidence * 100 | localNumber:'1.0-0' }}%
              </span>
              <span *ngIf="doc.confidence == null">—</span>
            </td>
            <td>{{ doc.page_count }}</td>
            <td>
              <span class="original-badge" [ngClass]="originalClass(doc)">
                {{ originalLabel(doc) }}
              </span>
            </td>
            <td class="actions-cell">
              <button class="btn-original"
                      [class.active]="doc.is_original === true"
                      [disabled]="savingOriginal === doc.oid"
                      (click)="setOriginal(doc, true)"
                      title="Mark as Original">
                ✓ {{ 'verification.markOriginal' | translate }}
              </button>
              <button class="btn-copy"
                      [class.active]="doc.is_original === false"
                      [disabled]="savingOriginal === doc.oid"
                      (click)="setOriginal(doc, false)"
                      title="Mark as Copy">
                ✗ {{ 'verification.markCopy' | translate }}
              </button>
              <button class="btn-clear"
                      *ngIf="doc.is_original != null"
                      [disabled]="savingOriginal === doc.oid"
                      (click)="setOriginal(doc, null)"
                      title="Clear assessment">
                ↺
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Checks tab -->
    <div class="tab-content" *ngIf="activeTab === 'checks'">
      <p class="tab-hint">{{ 'verification.checksHint' | translate }}</p>

      <!-- Stale examination (D4): the documents changed after the last run, so
           these results describe a document set that no longer exists. The
           re-run is offered, never taken automatically — it costs an LLM call. -->
      <div class="examination-stale" *ngIf="examinationStale">
        <span class="stale-icon">⚠</span>
        <span class="stale-text">
          {{ 'verification.examinationStale' | translate }}
          <em>({{ 'verification.examinationStaleSince' | translate }} {{ dossier?.examination_stale_at | localDateTime }})</em>
        </span>
        <button class="btn-auto-verify stale-rerun"
                [disabled]="autoVerifying || documents.length === 0"
                (click)="runAutoVerify()">
          {{ 'verification.examinationStaleRerun' | translate }}
        </button>
      </div>

      <!-- Auto-verify bar -->
      <div class="auto-verify-bar">
        <select [(ngModel)]="autoVerifyModel" class="model-select">
          <option value="">{{ 'verification.defaultModel' | translate }}</option>
          <option *ngFor="let m of complianceModels" [value]="m">{{ displayModel(m) }}</option>
        </select>
        <button class="btn-auto-verify"
                [disabled]="autoVerifying || documents.length === 0"
                (click)="runAutoVerify()">
          <span *ngIf="!autoVerifying">🤖 {{ 'verification.autoVerify' | translate }}</span>
          <span *ngIf="autoVerifying">{{ 'verification.autoVerifying' | translate }}</span>
        </button>
        <span class="auto-verify-hint">{{ 'verification.autoVerifyHint' | translate }}</span>
      </div>

      <!-- Auto-verify result -->
      <div class="auto-verify-result" *ngIf="autoVerifyResult">
        {{ 'verification.autoVerifyDone' | translate }}:
        {{ autoVerifyResult.auto_verified }} {{ 'verification.checks' | translate }}
        ({{ autoVerifyResult.usage.input + autoVerifyResult.usage.output }} tokens,
        {{ displayModel(autoVerifyResult.model) }})
      </div>
      <div class="error-message" *ngIf="autoVerifyError">{{ autoVerifyError }}</div>

      <div class="empty-state" *ngIf="checksByCategory.length === 0">
        {{ 'verification.noChecks' | translate }}
      </div>

      <div class="check-category" *ngFor="let cat of checksByCategory">
        <h3 class="category-title">{{ cat.category }}</h3>

        <div class="check-item" *ngFor="let check of cat.checks"
             [ngClass]="checkStatusClass(check.status)">

          <div class="check-main">
            <span class="check-status-icon">{{ checkStatusIcon(check.status) }}</span>
            <span class="check-description">{{ check.check_description }}</span>
            <span class="check-id">{{ check.check_id }}</span>
          </div>

          <div class="check-refs" *ngIf="check.catalog_refs?.length">
            <span class="ref-label">{{ 'verification.implementsRefs' | translate }}:</span>
            <span class="ref-badge" *ngFor="let ref of check.catalog_refs" [title]="catalogDesc(ref)">
              {{ ref }}<ng-container *ngIf="catalogUcp(ref)"> · {{ catalogUcp(ref) }}</ng-container>
            </span>
          </div>

          <div class="check-controls">
            <button class="btn-check ok" [class.active]="check.status === 'ok'"
                    [disabled]="savingCheck === check.check_id"
                    (click)="setCheckStatus(check, 'ok')" title="OK">✓</button>
            <button class="btn-check fail" [class.active]="check.status === 'fail'"
                    [disabled]="savingCheck === check.check_id"
                    (click)="setCheckStatus(check, 'fail')" title="Fail">✗</button>
            <button class="btn-check na" [class.active]="check.status === 'na'"
                    [disabled]="savingCheck === check.check_id"
                    (click)="setCheckStatus(check, 'na')" title="N/A">—</button>
            <button class="btn-check pending" [class.active]="check.status === 'pending'"
                    [disabled]="savingCheck === check.check_id"
                    (click)="setCheckStatus(check, 'pending')" title="Reset to pending">○</button>

            <button class="btn-notes" (click)="startEditNotes(check)"
                    [title]="'verification.editNotes' | translate">
              📝
            </button>
          </div>

          <!-- Notes display / edit -->
          <div class="check-notes" *ngIf="check.notes && editingNotes !== check.check_id">
            <span class="notes-label">{{ 'verification.notes' | translate }}:</span>
            {{ check.notes }}
          </div>
          <div class="check-notes-edit" *ngIf="editingNotes === check.check_id">
            <textarea [(ngModel)]="editNotesValue"
                      [placeholder]="'verification.notesPlaceholder' | translate"
                      rows="2"></textarea>
            <div class="notes-actions">
              <button class="btn-save-notes" (click)="saveNotes(check)">{{ 'verification.save' | translate }}</button>
              <button class="btn-cancel-notes" (click)="cancelEditNotes()">{{ 'verification.cancel' | translate }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TF Compliance Agent Chat (inline in checks tab) -->
    <div class="tf-chat-section" *ngIf="activeTab === 'checks' && tfAgentAvailable">
      <div class="tf-chat-toggle" (click)="toggleTfChat()">
        <span>🤖 {{ 'verification.tfAgent' | translate }}</span>
        <span class="tf-toggle-arrow">{{ tfChatOpen ? '▾' : '▸' }}</span>
      </div>

      <div class="tf-chat-panel" *ngIf="tfChatOpen">
        <div class="tf-chat-messages" #tfChatMessages>
          <div class="tf-chat-hint" *ngIf="tfMessages.length === 0">
            {{ 'verification.tfAgentHint' | translate }}
          </div>
          <div *ngFor="let msg of tfMessages"
               class="tf-msg" [class.user]="msg.role === 'user'" [class.assistant]="msg.role === 'assistant'">
            {{ msg.content }}
          </div>
          <div class="tf-typing" *ngIf="tfSending">
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          </div>
        </div>
        <div class="tf-chat-input-bar">
          <textarea class="tf-chat-input"
                    [(ngModel)]="tfInput"
                    (keydown)="onTfKeydown($event)"
                    [placeholder]="'verification.tfAgentPlaceholder' | translate"
                    [disabled]="tfSending"
                    rows="1"></textarea>
          <button class="tf-chat-send" (click)="sendTfMessage()" [disabled]="tfSending || !tfInput.trim()">➤</button>
        </div>
      </div>
    </div>

    <!-- Discrepancies tab (UCP-ISBP) -->
    <div class="tab-content" *ngIf="activeTab === 'discrepancies'">

      <!-- KPI: auto vs manual -->
      <div class="discrepancy-kpi">
        <div class="kpi-figure">
          <span class="kpi-value">{{ discrepancyKpi.total }}</span>
          <span class="kpi-label">{{ 'verification.discTotal' | translate }}</span>
        </div>
        <div class="kpi-split" *ngIf="discrepancyKpi.total > 0">
          <div class="kpi-bar">
            <div class="kpi-bar-auto" [style.width.%]="autoDetectedPercent"
                 [title]="'verification.discAuto' | translate"></div>
            <div class="kpi-bar-manual" [style.width.%]="manualDetectedPercent"
                 [title]="'verification.discManual' | translate"></div>
          </div>
          <div class="kpi-legend">
            <span class="legend-auto">{{ 'verification.discAuto' | translate }}: {{ discrepancyKpi.auto }} ({{ autoDetectedPercent }}%)</span>
            <span class="legend-manual">{{ 'verification.discManual' | translate }}: {{ discrepancyKpi.manual }} ({{ manualDetectedPercent }}%)</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="discrepancy-actions">
        <button class="btn-detect" (click)="runDetectDiscrepancies()" [disabled]="detectingDiscrepancies">
          {{ (detectingDiscrepancies ? 'verification.discDetecting' : 'verification.discDetect') | translate }}
        </button>
        <button class="btn-add-discrepancy" (click)="showAddDiscrepancy = !showAddDiscrepancy">
          + {{ 'verification.discAddManual' | translate }}
        </button>
      </div>

      <div class="discrepancy-error" *ngIf="discrepancyError">{{ discrepancyError }}</div>

      <!-- Manual add form -->
      <div class="discrepancy-add-form" *ngIf="showAddDiscrepancy">
        <label>{{ 'verification.discCatalog' | translate }}</label>
        <select [(ngModel)]="newDiscrepancyCatalogId" (ngModelChange)="onCatalogSelect()">
          <option value="">{{ 'verification.discFreeform' | translate }}</option>
          <option *ngFor="let e of discrepancyCatalog" [value]="e.id">{{ e.id }} — {{ e.description }}</option>
        </select>
        <label>{{ 'verification.discDescription' | translate }}</label>
        <textarea [(ngModel)]="newDiscrepancyDescription" rows="2"
                  [placeholder]="'verification.discDescriptionPlaceholder' | translate"></textarea>
        <div class="add-form-actions">
          <button class="btn-save-notes" (click)="addManualDiscrepancy()" [disabled]="addingDiscrepancy">
            {{ 'verification.save' | translate }}
          </button>
          <button class="btn-cancel-notes" (click)="showAddDiscrepancy = false">{{ 'verification.cancel' | translate }}</button>
        </div>
      </div>

      <!-- Discrepancy list -->
      <div class="discrepancy-empty" *ngIf="alerts.length === 0">
        {{ 'verification.discEmpty' | translate }}
      </div>

      <div class="discrepancy-list">
        <div class="discrepancy-item" *ngFor="let alert of alerts" [class.resolved]="alert.status === 'resolved'">
          <div class="discrepancy-main">
            <span class="discrepancy-source-badge" [class.manual]="alert.source === 'manual'">
              {{ (alert.source === 'manual' ? 'verification.discManual' : 'verification.discAuto') | translate }}
            </span>
            <span class="discrepancy-desc">{{ alert.description }}</span>
            <span class="discrepancy-rule" *ngIf="alert.rule_name && !isCatalogRule(alert.rule_name)">({{ alert.rule_name }})</span>
            <span class="ref-badge" *ngIf="isCatalogRule(alert.rule_name)" [title]="catalogDesc(alert.rule_name!)">
              {{ alert.rule_name }}<ng-container *ngIf="catalogUcp(alert.rule_name!)"> · {{ catalogUcp(alert.rule_name!) }}</ng-container>
            </span>
          </div>
          <div class="discrepancy-detail" *ngIf="alert.field_a || alert.value_a">
            <span *ngIf="alert.doc_a_type">{{ alert.doc_a_type }}</span>
            <span *ngIf="alert.field_a">· {{ alert.field_a }}</span>
            <span *ngIf="alert.value_a">= {{ alert.value_a }}</span>
          </div>
          <div class="discrepancy-status-buttons">
            <button [class.active]="alert.status === 'violation'" (click)="setAlertStatus(alert, 'violation')"
                    [disabled]="savingAlert === alert.oid">{{ 'verification.discViolation' | translate }}</button>
            <button [class.active]="alert.status === 'false_alarm'" (click)="setAlertStatus(alert, 'false_alarm')"
                    [disabled]="savingAlert === alert.oid">{{ 'verification.discFalseAlarm' | translate }}</button>
            <button [class.active]="alert.status === 'resolved'" (click)="setAlertStatus(alert, 'resolved')"
                    [disabled]="savingAlert === alert.oid">{{ 'verification.discResolved' | translate }}</button>
            <button [class.active]="alert.status === 'pending'" (click)="setAlertStatus(alert, 'pending')"
                    [disabled]="savingAlert === alert.oid">{{ 'verification.discPending' | translate }}</button>
          </div>
        </div>
      </div>

    </div>

  </ng-container>
</div>

<!-- Document Viewer Panel -->
<div class="viewer-panel" *ngIf="viewerOpen && viewerDoc" [style.width.px]="viewerPanelWidth">
  <div class="viewer-resize-handle" (mousedown)="onResizeStart($event)"></div>
  <div class="viewer-header">
    <select class="viewer-doc-select"
            [ngModel]="viewerDoc.oid"
            (ngModelChange)="selectViewerDoc($event)">
      <option *ngFor="let doc of documents" [value]="doc.oid">
        {{ doc.document_type || getUploadFilename(doc) }}
      </option>
    </select>
    <button class="viewer-close" (click)="closeViewer()">&times;</button>
  </div>
  <div class="viewer-pager">
    <button class="pager-btn" [disabled]="viewerPageIndex === 0" (click)="viewerPrevPage()">←</button>
    <span class="pager-label">{{ viewerPageIndex + 1 }} / {{ viewerPageCount }}</span>
    <button class="pager-btn" [disabled]="viewerPageIndex >= viewerPageCount - 1" (click)="viewerNextPage()">→</button>
  </div>
  <div class="viewer-body">
    <img *ngIf="viewerCurrentUrl" [src]="viewerCurrentUrl" class="viewer-image" alt="Page" />
    <div *ngIf="!viewerCurrentUrl" class="viewer-page-placeholder">{{ viewerPageIndex + 1 }}</div>
  </div>
</div>
</div>
